import { push, replace } from 'connected-react-router';
import { combineReducers } from 'redux';
import { call, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';

import { environment } from 'environments/environment';
import { CustomerDetail, CustomerSegment } from 'generated/bffCustomer/models';
import { AccessesToCustomerTree } from 'generated/bffUserAccess/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { EntityAccessManagementRoute } from 'helpers/routes/modules/accessManagement';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { store } from 'helpers/store/configureStore';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import {
  ClearContextEntityType,
  ClearContextRequestActionInputs,
  ClearContextSuccessActionInputs,
} from 'models/context/ClearContext';
import { ContextCustomerEntities } from 'models/context/ContextCustomerEntities';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';

import { UserInfo } from 'models/UserInfo';
import { getAccessesToCustomerTreeByCuRefNo, setLoadingAction } from 'saga/accessManagement/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { AuthActionTypes, selectUserInfo } from 'saga/auth/ducks';

import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { api as mktConsentsApi } from 'saga/marketingConsents/api';
import {
  canClearContextEntityCheck,
  clearContextAction,
  clearContextRequest,
  clearContextSuccess,
  ContextActionTypes,
  selectContextCustomerEntities,
  validateContextAction,
} from 'saga/context/ducks';
import { selectContextExternalUser, validateContextExternalAction } from 'saga/context/external/ducks';
import { selectContextInternalUser } from 'saga/context/internal/ducks';

/* STATE */
export interface ContextCustomerState {
  customer: CustomerInfo | null;
  customerList: CustomerInfo[];
}

/* ACTION TYPES */
export enum ContextCustomerActionTypes {
  SetContextCustomer = '@@Context/Customer/SET_CONTEXT_CUSTOMER',
  SetContextCustomerSkipped = '@@Context/Customer/SET_CONTEXT_CUSTOMER_SKIPPED',
  GetCustomerList = '@@Context/Customer/GET_CUSTOMER_LIST_INFO',
  UpdateCustomer = '@@Context/Customer/UPDATE_CUSTOMER',
  ValidateContextCustomer = '@@Context/Customer/VALIDATE_CONTEXT_CUSTOMER',
  ValidateContextCustomerCpConfirmation = '@@Context/Customer/VALIDATE_CONTEXT_CUSTOMER_CP_CONFIRMATION',
}

/* ACTIONS */
export const setContextCustomerActions = createApiActionCreators(ContextCustomerActionTypes.SetContextCustomer);
export const setContextCustomerSkippedAction = createActionCreator(
  ContextCustomerActionTypes.SetContextCustomerSkipped
);
export const getCustomerListActions = createApiActionCreators(ContextCustomerActionTypes.GetCustomerList);
export const updateCustomerAction = createApiActionCreators(ContextCustomerActionTypes.UpdateCustomer);
export const validateContextCustomerAction = createActionCreator(ContextCustomerActionTypes.ValidateContextCustomer);
export const validateContextCustomerCpConfirmationAction = createActionCreator(
  ContextCustomerActionTypes.ValidateContextCustomerCpConfirmation
);

/* REDUCERS */
const initialState: ContextCustomerState = {
  customer: null,
  customerList: [],
};

const customer = createReducer(initialState.customer, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerInfo | null, payload: ClearContextSuccessActionInputs) =>
      canClearContextEntityCheck(payload, ClearContextEntityType.CUSTOMER) ? null : _state,
    [RequestActionTypes.FAILURE]: (_state: CustomerInfo | null) => _state,
  },
  [ContextActionTypes.ValidateContext]: {
    [RequestActionTypes.FAILURE]: (_state: CustomerInfo, payload: ContextCustomerEntities) => payload.customer,
  },
  [ContextCustomerActionTypes.SetContextCustomer]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerInfo | null, payload: CustomerInfo) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
  [ContextCustomerActionTypes.UpdateCustomer]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerInfo | null) => {
      return {
        ..._state,
        primaryCPConfirmNeeded: false,
      };
    },
  },
});

const customerList = createReducer(initialState.customerList, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerInfo[], payload: ClearContextSuccessActionInputs) =>
      canClearContextEntityCheck(payload, ClearContextEntityType.CUSTOMER) ? [] : _state,
    [RequestActionTypes.FAILURE]: (_state: CustomerInfo[]) => _state,
  },
  [ContextCustomerActionTypes.GetCustomerList]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerInfo[], payload: CustomerInfo[]) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => [],
  },
});

export default combineReducers<ContextCustomerState>({
  customer,
  customerList,
});

/* SELECTORS */
const selectContextCustomer = (state: AppState) => state.context.customer || {};

export const selectContextCustomerInfo = (state: AppState): CustomerInfo | null =>
  selectContextCustomer(state).customer;

export const selectCustomerList = (state: AppState): CustomerInfo[] => selectContextCustomer(state).customerList;

/* SAGAS */
/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.ContextManagement.setContextCustomer.setContextCustomer
 * @param payload {@code {cuRefNo: string, forceUpdate: boolean, skipSharedObjectsSession: boolean}}}
 */
export function* setContextCustomer({ payload }: any) {
  if (!payload.cuRefNo) {
    const payloadInput: ClearContextRequestActionInputs = {
      skipSharedObjectsSession: !!payload.skipSharedObjectsSession,
      clearContextEntity: ClearContextEntityType.CUSTOMER,
      clearRelatedEntities: true,
    };

    yield put(clearContextAction.request(payloadInput));
    yield put(setContextCustomerActions.success(null));
  } else {
    const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
    const externalUser: ExternalUser = yield select(selectContextExternalUser);

    if (payload.forceUpdate || !contextCustomer || contextCustomer.cuRefNo !== payload.cuRefNo) {
      /*
       * based on APF-2472 changed condition
       * see detail in EA Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).setContextCustomer.check isTrustedFlag for in.cuRefNo
       */
      if (
        externalUser &&
        externalUser?.entityAccesses?.find((ea) => ea.cuRefNo === payload.cuRefNo && !ea.isTrusted && !ea.isVerified)
      ) {
        // redirect to WF 2.6.5
        store.dispatch(
          replace(
            `${buildRoute(ROUTES.VerifyEntityAccess)}?cpRefNo=${externalUser?.cpRefNo}&entityRefNo=${
              payload.cuRefNo
            }&entityType=Customer&redirectUrl=${
              window.location.pathname === EntityAccessManagementRoute.VerifyEntityAccess
                ? new URLSearchParams(window.location.search).get('redirectUrl')
                : window.location.href
            }`
          )
        );

        return;
      }

      // load Customer via API
      const contextExtUser: ExternalUser = yield select(selectContextExternalUser);
      const contextIntUser: InternalUser = yield select(selectContextInternalUser);
      const extUserCpRefNo = contextIntUser && contextExtUser ? contextExtUser.cpRefNo : undefined;

      if (
        contextIntUser &&
        contextExtUser &&
        !contextExtUser.entityAccesses?.find((ea) => ea.cuRefNo === payload.cuRefNo)
      ) {
        // Clear takeovered user from context
        yield call(clearContextSuccess, ClearContextEntityType.EXTERNAL_USER, false);
      }

      const request: ApiClientRequest = {
        apiCall: call(
          api.getCustomer,
          yield getApiUrl(environment.urlConfig.customer),
          payload.cuRefNo,
          extUserCpRefNo
        ),
      };
      const customerDetailResponse: ExtendedAxiosResponse = yield call(performApiCall, request);

      if (!customerDetailResponse.ok) {
        console.log('setContextCustomer failure: api.getCustomerList failed');
        yield put(setContextCustomerActions.failure());

        return;
      }

      const customerResponse: CustomerInfo | CustomerDetail | undefined = customerDetailResponse.data;

      // fail if no Customer found
      if (!customerResponse) {
        console.log(`setContextCustomer failure: no Customer found by ${payload.cuRefNo}`);
        yield put(setContextCustomerActions.failure());

        return;
      }

      // Get customer marketing consent status
      const getConsentsRespReq: ApiClientRequest = {
        apiCall: call(mktConsentsApi.getConsents, yield getApiUrl(environment.urlConfig.userMgw), payload.cuRefNo),
        skipDefaultKahlBehavior: true,
      };
      const getConsentsResp: ExtendedAxiosResponse = yield call(performApiCall, getConsentsRespReq);
      const mktConsentAgreed =
        getConsentsResp.status === 404 || // 404 means that Consent for given customer not found (consent was not registered yet)
        (getConsentsResp.ok && getConsentsResp.data?.consent.consentType === 'A');

      // clear context
      const clearContextInputs: ClearContextRequestActionInputs = {
        skipSharedObjectsSession: !!payload.skipSharedObjectsSession,
      };

      if (contextIntUser && contextExtUser?.entityAccesses?.find((ea) => ea.cuRefNo === payload.cuRefNo)) {
        clearContextInputs.clearContextEntity = ClearContextEntityType.CUSTOMER;
      }

      /*
       * current context of customer is empty, not need to clear
       * related problems: APF-4258, APF-1941
       */
      if (contextCustomer) {
        // waiting for clearContextRequest to be done
        yield call(clearContextRequest, clearContextInputs);
      }

      // SharedObjects synchronization
      if (!payload.skipSharedObjectsSession && (payload.forceUpdate || contextCustomer?.cuRefNo !== payload.cuRefNo)) {
        const request: ApiClientRequest = {
          apiCall: call(api.postCustomerContext, yield getApiUrl(environment.urlConfig.userContext), payload.cuRefNo),
        };
        const postCuContextResp: ExtendedAxiosResponse = yield call(performApiCall, request);

        if (!postCuContextResp.ok) {
          console.log('setContextCustomer failure: api.postCustomerContext failed');
          yield put(setContextCustomerActions.failure());

          return;
        }
      }

      // set new Customer to context
      const newContextCustomer: CustomerInfo = {
        cuRefNo: customerResponse.cuRefNo || '',
        displayName: customerResponse.displayName,
        segment: customerResponse.segment as CustomerSegment,
        subjectType: customerResponse.subjectType,
        customerCareLevel: customerResponse.customerCareLevel,
        marketingAgreement: mktConsentAgreed,
        primaryCPConfirmNeeded: customerResponse.primaryCPConfirmNeeded,
      };

      if (customerResponse.primaryAdmin) {
        newContextCustomer.primaryAdmin = {
          cpRefNo: customerResponse.primaryAdmin.cpRefNo,
          displayName: customerResponse.primaryAdmin.displayName,
          email: customerResponse.primaryAdmin.email,
          firstName: customerResponse.primaryAdmin.firstName,
          lastName: customerResponse.primaryAdmin.lastName,
          phoneNoMobile: customerResponse.primaryAdmin.phoneNoMobile,
        };
      }

      yield put(setContextCustomerActions.success(newContextCustomer));
    } else {
      console.info(`setContextCustomer skipped: contextCustomer[cuRefNo=${payload.cuRefNo}] already exists.`);
      yield put(setContextCustomerSkippedAction());
    }
  }
}

const isBacklink = () => {
  let backlink = false;

  if (window.location.href.indexOf('backlink') !== -1) {
    backlink = true;
  }

  window.onpopstate = () => {
    backlink = true;
  };

  return backlink;
};

function* validateContextReturnSuccess() {
  // validateContext: return success
  yield putResolve(validateContextCustomerAction());
  yield putResolve(validateContextAction.success());
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).validateContext.validateContext
 * Part: setContextCustomer
 */
function* validateContextSetContextCustomer({ payload }: any) {
  if (!payload.cuRefNo) {
    return;
  }

  const contextEntitiesBackup: ContextCustomerEntities = yield select(selectContextCustomerEntities);

  if (payload.cleanContextFirst) {
    // also TARIFF_SPACE and ASSET is removed
    yield call(clearContextSuccess, ClearContextEntityType.CUSTOMER_ACCOUNT);
  }

  // set context customer
  yield putResolve(setContextCustomerActions.request(payload));

  // waiting for context customer to be set
  yield takeLatest(
    createActionType(ContextCustomerActionTypes.SetContextCustomer, RequestActionTypes.SUCCESS),
    validateContextReturnSuccess
  );
  yield takeLatest(
    createActionType(ContextCustomerActionTypes.SetContextCustomerSkipped),
    validateContextReturnSuccess
  );

  yield takeLatest(
    createActionType(ContextCustomerActionTypes.SetContextCustomer, RequestActionTypes.FAILURE),
    function* () {
      // validateContext: set back original context state
      console.log('validateContext failure: SetContextCustomer failed - set back original context state');
      yield put(validateContextAction.failure(contextEntitiesBackup));
    }
  );
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).validateContext.validateContext
 * Part: validateContextCustomer
 */
export function* validateContextCustomer() {
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const contextExternalUser: ExternalUser = yield select(selectContextExternalUser);
  const contextInternalUser: InternalUser = yield select(selectContextInternalUser);

  if (contextCustomer) {
    // Display primary CP confirmation page?
    yield putResolve(validateContextCustomerCpConfirmationAction());
    yield putResolve(validateContextExternalAction());
  } else if (
    /*
     * Customer context required ?
     * currently all other modules/pages requires customer context
     * anonym user is not possible now
     */
    [
      ROUTES.SendingCreation.toString(),
      ROUTES.VerifyEntityAccess.toString(),
      ROUTES.AllInvoices.toString(),
      ROUTES.Home.toString(),
      ROUTES.DocumentFilter.toString(),
    ].includes(window.location.pathname) ||
    //url for document Detail - document detail url is same as documentList url + 2 params separated by '/'
    window.location.href?.split(`${ROUTES.DocumentList}/`)[1]?.split('/')?.length > 1
  ) {
    // Continue to validateContextExternalUser
    yield put(validateContextExternalAction());
  }
  // contextCustomer is required
  else if (contextExternalUser) {
    /*
     * Load list of customers selectable to user's context
     * Direct call
     */
    const request: ApiClientRequest = {
      apiCall: call(
        api.getCustomerList,
        yield getApiUrl(environment.urlConfig.customer),
        contextInternalUser && contextExternalUser ? contextExternalUser.cpRefNo : undefined
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);
    const contextCustomersList: CustomerInfo[] = resp?.data?.customerList;

    if (!contextCustomersList) {
      yield put(setContextCustomerActions.failure());
    } else if (contextCustomersList.length === 1) {
      // Set customerContext
      yield put(
        validateContextAction.request({
          cuRefNo: contextCustomersList[0].cuRefNo,
        })
      );
    } else {
      // Go to customer selection 2.6.1.1
      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: LegacyCrmDeeplinkCommand.EMO2CUSELECT,
        queryParams: {
          customerContextRedirectUrl: encodeURIComponent(window.location.href),
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    }
  } else {
    // Go to internal dashboard 4.2.1
    const deeplinkParams: LegacyCrmDeeplinkConfig = {
      command: LegacyCrmDeeplinkCommand.IMO2DASH,
      queryParams: {
        page: 'internalDashboard',
      },
    };

    yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
  }
}

function* validateContextCustomerCpConfirmation() {
  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const contextExternalUser: ExternalUser = yield select(selectContextExternalUser);
  const contextInternalUser: InternalUser = yield select(selectContextInternalUser);

  // Display primary CP confirmation page?
  if (
    !contextInternalUser &&
    contextExternalUser?.cpRefNo === contextCustomer?.primaryAdmin?.cpRefNo &&
    contextCustomer?.segment !== 'R' &&
    contextCustomer?.primaryCPConfirmNeeded &&
    !isBacklink()
  ) {
    const accessesToCustomerTree: AccessesToCustomerTree = yield call(
      getAccessesToCustomerTreeByCuRefNo,
      contextCustomer.cuRefNo
    );

    if (accessesToCustomerTree?.accessesList?.length > 1) {
      if (window.location.pathname !== EntityAccessManagementRoute.PrimaryContactPersonConfirmation) {
        yield putResolve(push(buildRoute(EntityAccessManagementRoute.PrimaryContactPersonConfirmation)));
      }
    }
  }
}

function* getCustomerList({ payload: cpRefNo }: any) {
  const loggedUser: UserInfo | null = yield select(selectUserInfo);

  if (!loggedUser?.logged) {
    return;
  }

  const request: ApiClientRequest = {
    apiCall: call(api.getCustomerList, yield getApiUrl(environment.urlConfig.customer), cpRefNo),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getCustomerListActions.success(resp.data?.customerList || []));
  } else {
    console.log('getCustomerList failure: api.getCustomerList failed');
    yield put(getCustomerListActions.failure());
  }
}

function* updateCustomer({ payload }: AppAction<CustomerInfo>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const requestBody: CustomerInfo = {
    cuRefNo: payload.cuRefNo,
    primaryCPConfirmNeeded: false,
  };
  const request: ApiClientRequest = {
    apiCall: call(api.updateCustomer, yield getApiUrl(environment.urlConfig.customer), payload.cuRefNo, requestBody),
  };

  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(updateCustomerAction.success());
    yield put(push(buildRoute(EntityAccessManagementRoute.ContactPersons)));
  } else {
    yield put(updateCustomerAction.failure());
  }
}

/* EXPORT */
export function* contextCustomerSaga() {
  yield takeLatest(
    createActionType(ContextCustomerActionTypes.SetContextCustomer, RequestActionTypes.REQUEST),
    setContextCustomer
  );

  yield takeLatest(
    createActionType(ContextActionTypes.ValidateContext, RequestActionTypes.REQUEST),
    validateContextSetContextCustomer
  );

  yield takeLatest(
    createActionType(ContextCustomerActionTypes.GetCustomerList, RequestActionTypes.REQUEST),
    getCustomerList
  );

  yield takeLatest(
    createActionType(ContextCustomerActionTypes.UpdateCustomer, RequestActionTypes.REQUEST),
    updateCustomer
  );
  yield takeLatest(createActionType(ContextCustomerActionTypes.ValidateContextCustomer), validateContextCustomer);
  yield takeLatest(
    createActionType(ContextCustomerActionTypes.ValidateContextCustomerCpConfirmation),
    validateContextCustomerCpConfirmation
  );
}
